import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, Table, Pagination, Badge, Modal } from '@/components/ui';
import type { Column, SortDirection } from '@/components/ui';
import { adminService } from '@/services/admin.service';
import type { TopupRequestAdmin } from '@/services/admin.service';

type TopupStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

const STATUS_OPTIONS: { value: TopupStatus | ''; label: string }[] = [
  { value: '', label: 'All Status' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

const getStatusBadgeVariant = (status: TopupStatus) => {
  switch (status) {
    case 'APPROVED':
      return 'success';
    case 'REJECTED':
      return 'danger';
    default:
      return 'warning';
  }
};

export const TopupRequests: React.FC = () => {
  const [requests, setRequests] = useState<TopupRequestAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filters
  const [status, setStatus] = useState<TopupStatus | ''>('PENDING');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Sorting
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Modal state
  const [selectedRequest, setSelectedRequest] = useState<TopupRequestAdmin | null>(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Detail modal
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await adminService.getTopupRequests(
        status || undefined,
        currentPage,
        itemsPerPage
      );
      setRequests(response.data);
      setTotalPages(response.meta.totalPages);
      setTotalItems(response.meta.total);
    } catch {
      setError('Failed to load topup requests');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, status]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    setIsProcessing(true);
    try {
      await adminService.approveTopup(selectedRequest.id, approveNotes || undefined);
      setIsApproveModalOpen(false);
      setSelectedRequest(null);
      setApproveNotes('');
      setSuccessMessage('Topup request approved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchRequests();
    } catch {
      setError('Failed to approve topup request');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectReason.trim()) return;

    setIsProcessing(true);
    try {
      await adminService.rejectTopup(selectedRequest.id, rejectReason);
      setIsRejectModalOpen(false);
      setSelectedRequest(null);
      setRejectReason('');
      setSuccessMessage('Topup request rejected');
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchRequests();
    } catch {
      setError('Failed to reject topup request');
    } finally {
      setIsProcessing(false);
    }
  };

  const openApproveModal = (request: TopupRequestAdmin) => {
    setSelectedRequest(request);
    setApproveNotes('');
    setIsApproveModalOpen(true);
  };

  const openRejectModal = (request: TopupRequestAdmin) => {
    setSelectedRequest(request);
    setRejectReason('');
    setIsRejectModalOpen(true);
  };

  const columns: Column<TopupRequestAdmin>[] = [
    {
      key: 'tenantName',
      header: 'Tenant',
      sortable: true,
      render: (request) => (
        <Link
          to={`/tenants/${request.tenantId}`}
          className="font-medium text-primary-600 hover:text-primary-700"
          onClick={(e) => e.stopPropagation()}
        >
          {request.tenantName}
        </Link>
      ),
    },
    {
      key: 'requestedBy',
      header: 'Requested By',
      render: (request) => (
        <div>
          <p className="text-sm text-secondary-900">
            {request.requestedBy.firstName} {request.requestedBy.lastName}
          </p>
          <p className="text-xs text-secondary-500">{request.requestedBy.email}</p>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      render: (request) => (
        <span className="text-lg font-semibold text-secondary-900">
          ${request.amount.toFixed(2)}
        </span>
      ),
    },
    {
      key: 'currentBalance',
      header: 'Current Balance',
      render: (request) => (
        <div>
          <span className="text-sm font-medium text-secondary-700">
            ${(request.userCurrentBalance ?? 0).toFixed(2)}
          </span>
          <p className="text-xs text-secondary-500">
            After: ${((request.userCurrentBalance ?? 0) + request.amount).toFixed(2)}
          </p>
        </div>
      ),
    },
    {
      key: 'paymentMethod',
      header: 'Payment',
      render: (request) => (
        <div>
          <span className="text-sm text-secondary-700">{request.paymentMethod}</span>
          {request.paymentReference && (
            <p className="text-xs text-secondary-500">Ref: {request.paymentReference}</p>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (request) => (
        <Badge variant={getStatusBadgeVariant(request.status)} dot>
          {request.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Requested',
      sortable: true,
      render: (request) => (
        <span className="text-secondary-500">
          {new Date(request.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (request) => (
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRequest(request);
              setIsDetailModalOpen(true);
            }}
          >
            View
          </Button>
          {request.status === 'PENDING' && (
            <>
              <Button
                size="sm"
                variant="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  openApproveModal(request);
                }}
              >
                Approve
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={(e) => {
                  e.stopPropagation();
                  openRejectModal(request);
                }}
              >
                Reject
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-success-50 border border-success-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-success-700">{successMessage}</p>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-success-500 hover:text-success-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Topup Requests</h1>
          <p className="mt-1 text-sm text-secondary-500">
            Review and process wallet topup requests ({totalItems} total)
          </p>
        </div>
        {status === 'PENDING' && requests.length > 0 && (
          <Badge variant="warning" size="lg">
            {requests.length} pending request{requests.length > 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as TopupStatus | '');
              setCurrentPage(1);
            }}
            className="px-4 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="flex items-center space-x-4 text-sm text-secondary-600">
            <span className="flex items-center">
              <span className="w-2 h-2 bg-warning-500 rounded-full mr-2" />
              Pending: Awaiting review
            </span>
            <span className="flex items-center">
              <span className="w-2 h-2 bg-success-500 rounded-full mr-2" />
              Approved: Funds added
            </span>
            <span className="flex items-center">
              <span className="w-2 h-2 bg-danger-500 rounded-full mr-2" />
              Rejected: Request denied
            </span>
          </div>
        </div>
      </Card>

      {/* Error State */}
      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-danger-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-danger-500 hover:text-danger-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <Table
        columns={columns}
        data={requests}
        keyExtractor={(request) => request.id}
        isLoading={isLoading}
        emptyMessage="No topup requests found"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        onRowClick={(request) => {
          setSelectedRequest(request);
          setIsDetailModalOpen(true);
        }}
      />

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedRequest(null);
        }}
        title="Topup Request Details"
        size="lg"
      >
        {selectedRequest && (
          <>
            <Modal.Body>
              <div className="space-y-6">
                {/* Amount Highlight */}
                <div className="text-center p-6 bg-secondary-50 rounded-lg">
                  <p className="text-sm text-secondary-500 mb-1">Topup Amount</p>
                  <p className="text-4xl font-bold text-secondary-900">
                    ${selectedRequest.amount.toFixed(2)}
                  </p>
                  <Badge
                    variant={getStatusBadgeVariant(selectedRequest.status)}
                    className="mt-2"
                  >
                    {selectedRequest.status}
                  </Badge>
                </div>

                {/* Balance Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-primary-50 rounded-lg">
                  <div>
                    <p className="text-sm text-primary-600">Current Balance</p>
                    <p className="text-xl font-semibold text-primary-900">
                      ${(selectedRequest.userCurrentBalance ?? 0).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-primary-600">Balance After Approval</p>
                    <p className="text-xl font-semibold text-primary-900">
                      ${((selectedRequest.userCurrentBalance ?? 0) + selectedRequest.amount).toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Details Grid */}
                <dl className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">Tenant</dt>
                    <dd className="mt-1">
                      <Link
                        to={`/tenants/${selectedRequest.tenantId}`}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        {selectedRequest.tenantName}
                      </Link>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">Payment Method</dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {selectedRequest.paymentMethod}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">Payment Reference</dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {selectedRequest.paymentReference || 'N/A'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">Requested By</dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      <Link
                        to={`/users/${selectedRequest.requestedBy.id}`}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        {selectedRequest.requestedBy.firstName}{' '}
                        {selectedRequest.requestedBy.lastName}
                      </Link>
                      <br />
                      <span className="text-secondary-500">
                        {selectedRequest.requestedBy.email}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">Requested At</dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {new Date(selectedRequest.createdAt).toLocaleString()}
                    </dd>
                  </div>
                  {selectedRequest.processedAt && (
                    <div>
                      <dt className="text-sm font-medium text-secondary-500">Processed At</dt>
                      <dd className="mt-1 text-sm text-secondary-900">
                        {new Date(selectedRequest.processedAt).toLocaleString()}
                      </dd>
                    </div>
                  )}
                  {selectedRequest.processedBy && (
                    <div className="col-span-2">
                      <dt className="text-sm font-medium text-secondary-500">Processed By</dt>
                      <dd className="mt-1 text-sm text-secondary-900">
                        {selectedRequest.processedBy.firstName}{' '}
                        {selectedRequest.processedBy.lastName}
                      </dd>
                    </div>
                  )}
                  {selectedRequest.notes && (
                    <div className="col-span-2">
                      <dt className="text-sm font-medium text-secondary-500">Notes</dt>
                      <dd className="mt-1 text-sm text-secondary-900 p-3 bg-secondary-50 rounded-lg">
                        {selectedRequest.notes}
                      </dd>
                    </div>
                  )}
                  {selectedRequest.proofUrl && (
                    <div className="col-span-2">
                      <dt className="text-sm font-medium text-secondary-500">Payment Proof</dt>
                      <dd className="mt-1">
                        <a
                          href={selectedRequest.proofUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-primary-600 hover:text-primary-700"
                        >
                          <svg
                            className="w-4 h-4 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                          View Document
                        </a>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => {
                  setIsDetailModalOpen(false);
                  setSelectedRequest(null);
                }}
              >
                Close
              </Button>
              {selectedRequest.status === 'PENDING' && (
                <>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setIsDetailModalOpen(false);
                      openRejectModal(selectedRequest);
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => {
                      setIsDetailModalOpen(false);
                      openApproveModal(selectedRequest);
                    }}
                  >
                    Approve
                  </Button>
                </>
              )}
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/* Approve Modal */}
      <Modal
        isOpen={isApproveModalOpen}
        onClose={() => {
          setIsApproveModalOpen(false);
          setSelectedRequest(null);
          setApproveNotes('');
        }}
        title="Approve Topup Request"
        size="sm"
      >
        <Modal.Body>
          <div className="space-y-4">
            <div className="p-4 bg-success-50 rounded-lg">
              <p className="text-success-700">
                Approving this will add{' '}
                <span className="font-bold">${selectedRequest?.amount.toFixed(2)}</span> to{' '}
                <span className="font-bold">{selectedRequest?.tenantName}</span>'s wallet.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                Notes (Optional)
              </label>
              <textarea
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Add any notes about this approval..."
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsApproveModalOpen(false);
              setSelectedRequest(null);
              setApproveNotes('');
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleApprove} isLoading={isProcessing}>
            Approve Topup
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={isRejectModalOpen}
        onClose={() => {
          setIsRejectModalOpen(false);
          setSelectedRequest(null);
          setRejectReason('');
        }}
        title="Reject Topup Request"
        size="sm"
      >
        <Modal.Body>
          <div className="space-y-4">
            <div className="p-4 bg-danger-50 rounded-lg">
              <p className="text-danger-700">
                Rejecting this request for{' '}
                <span className="font-bold">${selectedRequest?.amount.toFixed(2)}</span> from{' '}
                <span className="font-bold">{selectedRequest?.tenantName}</span>.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                Reason for Rejection <span className="text-danger-500">*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Provide a reason for rejection..."
                required
              />
              {rejectReason.trim() === '' && (
                <p className="mt-1 text-xs text-danger-500">
                  A reason is required to reject a request
                </p>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsRejectModalOpen(false);
              setSelectedRequest(null);
              setRejectReason('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleReject}
            isLoading={isProcessing}
            disabled={!rejectReason.trim()}
          >
            Reject Request
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TopupRequests;
